import type { AxiosInstance, AxiosProgressEvent, AxiosResponse } from 'axios';

import type ApiRequestOptions from '../../api-request/ApiRequestOptions';
import type ApiResponse from '../../api-response/ApiResponse';
import type ApiClientResponseInterceptor from '../api-client-interceptors/ApiClientResponseInterceptor';

import axios from 'axios';
import { omit } from 'lodash-es';

import DefaultLogger from '../../../../lib/debug/logger/DefaultLogger';
import extractErrorMessageFromApiError from '../api-client-errors/extractErrorMessageFromApiError';
import getErrorMessageByStatus from '../api-client-errors/getErrorMessageByStatus';

function makeNonApiErrorResponse(error: unknown): ApiResponse {
  return {
    errorMessage: async () => `Non-api error. ${error}`,
    json: () => {
      throw new Error(`Non-api error. ${error}`);
    },
    ok: false,
    status: 0,
  };
}

export default async function requestViaAxios(
  axiosInstance: AxiosInstance,
  url: string,
  options: ApiRequestOptions & {
    baseUrl?: string;
    responseInterceptors?: ApiClientResponseInterceptor[];
  },
): Promise<ApiResponse> {
  const applyResponseInterceptors = async (response: ApiResponse): Promise<ApiResponse> => {
    if (!options.responseInterceptors) {
      return response;
    }

    let resultResponse = response;

    for (const interceptor of options.responseInterceptors) {
      try {
        resultResponse = await interceptor(response, url, omit(options, 'baseUrl', 'responseInterceptor'));
      } catch (error) {
        DefaultLogger.writeError('Got error during response interceptor', error);
      }
    }

    return resultResponse;
  };

  const makeAxiosOnUploadProgressHandler = () => {
    const { onUploadProgress } = options;

    if (!onUploadProgress) {
      return;
    }

    return (uploadProgressEvent: AxiosProgressEvent) => {
      try {
        onUploadProgress({
          progress:
            uploadProgressEvent.total == null || uploadProgressEvent.total === 0
              ? uploadProgressEvent.loaded
              : uploadProgressEvent.loaded / uploadProgressEvent.total,
          sentBytes: uploadProgressEvent.loaded,
        });
      } catch (error) {
        // уже и не помню, для чего вообще здесь нужен этот try catch
        DefaultLogger.writeError(error);
      }
    };
  };

  try {
    const axiosOnUploadProgressHandler = makeAxiosOnUploadProgressHandler();

    const response = await axiosInstance.request({
      adapter: axiosOnUploadProgressHandler == null ? 'fetch' : 'xhr',
      baseURL: options.baseUrl ?? undefined,
      data: options.json ?? options.body,
      method: options.method ?? 'get',
      onUploadProgress: axiosOnUploadProgressHandler,
      params: options.params,
      signal: options.signal,
      url,
    });

    return await applyResponseInterceptors({
      json: async <T>() => {
        return response.data as T;
      },
      ok: true,
      status: response.status,
    });
  } catch (error) {
    if (!axios.isAxiosError(error) || !error.response) {
      return applyResponseInterceptors(makeNonApiErrorResponse(error));
    }

    const response: AxiosResponse<unknown, unknown> = error.response;

    const errorResponseStatus = response?.status ?? 0;
    const errorResponseJson = response?.data ?? null;

    return applyResponseInterceptors({
      errorMessage: async () =>
        getErrorMessageByStatus(errorResponseStatus) ?? extractErrorMessageFromApiError(errorResponseJson),
      json: <T>() => errorResponseJson as T,
      ok: false,
      status: errorResponseStatus,
    });
  }
}
